import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const contractorBadgeResponseSchema = z.object({
  data: z.array(
    z.object({
      type: z.literal("contractor-badge"),
      id: z.string().min(1),
      attributes: z.object({
        qualification: z.object({
          displayLabel: z.string().min(1),
          name: z.string().min(1),
        }),
        license: z
          .object({
            number: z.string(),
            state: z.string(),
            isMultiState: z.boolean(),
          })
          .optional(),
      }),
    })
  ),
});

export type ContractorBadgeResponse = z.infer<typeof contractorBadgeResponseSchema>;
export type ContractorBadgeData = ContractorBadgeResponse["data"][number];
interface GetContractorBadgesParams {
  workerId: string;
  qualification?: string;
  state?: string;
}

export function getContractorBadgesUrl(): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/contractor-badges`;
}

export function resetContractorBadgesQuery(queryClient: QueryClient) {
  void queryClient.resetQueries({
    queryKey: [getContractorBadgesUrl()],
  });
}

export function useGetContractorBadges(
  params: GetContractorBadgesParams,
  options: UseGetQueryOptions<ContractorBadgeResponse> = {}
): UseQueryResult<ContractorBadgeResponse> {
  const { workerId, qualification, state } = params;

  return useGetQuery({
    url: getContractorBadgesUrl(),
    queryParams: {
      filter: {
        workerId,
        ...(qualification && { qualification }),
        ...(state && { state }),
      },
    },
    responseSchema: contractorBadgeResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_FETCH_FAILURE,
    },
    enabled: Boolean(workerId),
    ...options,
  });
}
